import {Icon, Show} from 'platform/foundation';

import {toPairs} from 'ramda';
import {isNonEmptyArray} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {getSafely, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useRentalCalendarFilterOptions} from '../hooks/useRentalCalendarFilterOptions';
import {
  EMPTY_RENTAL_CALENDAR_FILTERS,
  type RentalCalendarFilterValues,
} from '../types/RentalCalendarFilterValues';
import {findFilterOptionLabel} from '../utils/findFilterOptionLabel';
import {getRentalCalendarFilterLabel} from '../utils/getRentalCalendarFilterLabel';
import {setRentalCalendarFilter} from '../utils/setRentalCalendarFilter';

interface RentalCalendarActiveFiltersProps extends RequiredTestIdProps {
  filters: RentalCalendarFilterValues;
  onFiltersChange: (filters: RentalCalendarFilterValues) => void;
}

export function RentalCalendarActiveFilters(props: RentalCalendarActiveFiltersProps) {
  const {options, isLoading} = useRentalCalendarFilterOptions(props.filters);

  // One chip per selected option, so a single value can be removed without opening the panel
  const selectedOptions = toPairs(props.filters).flatMap(([key, selected]) =>
    selected.map((value) => ({
      key,
      value,
      label: findFilterOptionLabel(getSafely(options, key) ?? [], value),
    }))
  );

  const removeValue = (key: keyof RentalCalendarFilterValues, value: string) =>
    props.onFiltersChange(
      setRentalCalendarFilter(
        props.filters,
        key,
        (getSafely(props.filters, key) ?? []).filter((selected) => selected !== value)
      )
    );

  return (
    <Show when={!isLoading && isNonEmptyArray(selectedOptions)}>
      <div
        className="flex shrink-0 flex-wrap items-center gap-2 bg-palettes-blue-10-100 px-3 py-2"
        data-testid={suffixTestId('activeFilters', props)}
      >
        <Icon size={4} value="content/filter_list" color="palettes.black.900.70" />
        <span className="text-xs font-bold text-secondary">
          {i18n.t('page.datagrid.labels.filteredBy')}
        </span>

        {selectedOptions.map(({key, value, label}) => (
          <span
            key={`${key}-${value}`}
            className="flex items-center gap-1 rounded bg-palettes-white-10-100 py-1 pl-2 pr-1 text-xs"
            data-testid={suffixTestId(`activeFilters-${key}-${value}`, props)}
          >
            <span className="font-bold text-primary">{getRentalCalendarFilterLabel(key)}</span>
            <span className="text-secondary">{label}</span>
            <button
              type="button"
              aria-label={label}
              onClick={() => removeValue(key, value)}
              className="flex cursor-pointer items-center bg-transparent p-0"
              data-testid={suffixTestId(`activeFilters-${key}-${value}-remove`, props)}
            >
              <Icon size={4} value="navigation/close" color="palettes.black.900.70" />
            </button>
          </span>
        ))}

        <button
          type="button"
          onClick={() => props.onFiltersChange(EMPTY_RENTAL_CALENDAR_FILTERS)}
          className="ml-auto cursor-pointer bg-transparent text-xs font-bold text-link"
          data-testid={suffixTestId('activeFilters-clearAll', props)}
        >
          {i18n.t('page.datagrid.labels.clearAll')}
        </button>
      </div>
    </Show>
  );
}
